"use client";

import Image from "next/image";
import { useRef, useState } from "react";

function sampleBrightnessFromElement(img: HTMLImageElement): number | null {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0);

    let imageData: ImageData;
    try {
        imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch {
        // Cross-origin image without CORS headers taints the canvas —
        // bail out silently rather than fetching a second copy.
        return null;
    }
    const pixels = imageData.data;

    let totalBrightness = 0;
    let totalPixels = 0;

    for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];

        // Step 1: Grayscale = average
        let gray = (r + g + b) / 3;

        // Step 2: Contrast(200%) around midpoint 128
        gray = (gray - 128) * 2 + 128;
        gray = Math.max(0, Math.min(255, gray)); // Clamp to 0-255

        totalBrightness += gray;
        totalPixels++;
    }

    return totalBrightness / totalPixels;
}

export default function LogoImage({
    src,
    alt,
    className,
    width = 40,
    height = 40,
    objectFit = "cover",
    applyDarkLightFilter = true,
}: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    objectFit?: "cover" | "contain";
    /**
     * The dark/light auto-invert filter is for logos placed directly on a
     * dark background (e.g. the portfolio card overlay). Callers that already
     * control greyscale/hover via their own CSS (e.g. ClientTicker, which
     * toggles `grayscale`/`grayscale-0` on hover) must disable this — an
     * inline `filter` here would be permanent and fight that hover effect.
     */
    applyDarkLightFilter?: boolean;
}) {
    const [isDark, setIsDark] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    // Sample brightness from the already-rendered, already-loaded next/image
    // element itself (no second network request, no Cloudinary transform —
    // transforms cost Cloudinary credits and must not be used for this).
    const handleLoad = () => {
        if (!applyDarkLightFilter) return;
        const img = imgRef.current;
        if (!img) return;
        const avgBrightness = sampleBrightnessFromElement(img);
        if (avgBrightness !== null) setIsDark(avgBrightness < 50);
    };

    return (
        <Image
            ref={imgRef}
            src={src}
            alt={alt}
            width={width}
            height={height}
            className={className}
            crossOrigin="anonymous"
            onLoad={handleLoad}
            style={{
                filter: applyDarkLightFilter
                    ? isDark
                        ? "grayscale(100%) contrast(200%) invert(100%)"
                        : "grayscale(100%) contrast(200%)"
                    : undefined,
                objectFit,
            }}
        />
    );
}
